export interface FaqSection {
  type: "h2" | "h3" | "p" | "ul" | "ol" | "callout" | "numbered";
  content: string | string[];
  heading?: string;
}

export interface FaqPair {
  q: string;
  a: string;
}

export interface FaqPage {
  slug: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  publishedDate: string;
  modifiedDate: string;
  excerpt: string;
  sections: FaqSection[];
  faqs: FaqPair[];
  relatedLinks?: { href: string; title: string }[];
}

export const FAQ_PAGES: FaqPage[] = [
  {
    slug: "how-accurate-is-ai-estimating",
    title: "How Accurate Is AI Estimating for UK Builders?",
    metaTitle: "How Accurate Is AI Estimating? (UK Builder's Guide 2026)",
    metaDescription:
      "How accurate is AI estimating for UK builders? An honest breakdown of accuracy ranges, the factors that affect reliability, and when to trust AI-generated quotes.",
    publishedDate: "2026-06-30",
    modifiedDate: "2026-06-30",
    excerpt:
      "AI estimating tools can produce a first-draft construction quote in minutes. But how close are those numbers to reality? Here is an honest look at accuracy ranges, what affects them, and when to trust the output.",
    sections: [
      {
        type: "p",
        content:
          "AI estimating for construction is no longer experimental. Builders across the UK are using AI-powered tools to produce structured scopes and pricing from drawings, photos, and plain-text descriptions. The natural question is: how close does the output actually get to the real cost?",
      },
      {
        type: "p",
        content:
          "The honest answer is that accuracy varies. Understanding what drives that variation is more useful than any single headline figure. This guide walks through what AI estimating can and cannot do, the factors that affect reliability, and how to use AI-generated quotes safely.",
      },
      {
        type: "h2",
        content: "What does 'accuracy' mean in construction estimating?",
      },
      {
        type: "p",
        content:
          "In construction, an estimate is not a fixed price. It is a structured prediction. Even experienced quantity surveyors producing detailed cost plans from full drawings expect outturn costs to land within a tolerance band. The RICS definition of a formal cost plan at RIBA Stage 3 (developed design) carries a typical tolerance of plus or minus 10 to 15 percent. Earlier-stage estimates, produced from outline information, carry plus or minus 25 percent or more.",
      },
      {
        type: "p",
        content:
          "AI estimating tools sit closer to the early-stage end of this spectrum when given limited input, and closer to the Stage 3 end when given detailed drawings and a clear specification. Judging AI accuracy against the final certified cost of a completed project sets an unrealistic bar, the same bar that catches most pre-contract estimates.",
      },
      {
        type: "callout",
        content:
          "A well-configured AI estimating tool, given architectural drawings and a clear project description, typically produces totals within 10 to 20 percent of a manually prepared estimate for the same scope. On straightforward residential jobs, the gap is often under 10 percent.",
      },
      {
        type: "h2",
        content: "Factors that affect AI estimating accuracy",
      },
      {
        type: "h3",
        content: "1. Quality and completeness of the input",
      },
      {
        type: "p",
        content:
          "AI estimating tools extract information from what you give them. A full set of architectural drawings with dimensions, a structural engineer's report, and a written specification will produce a far more accurate output than a rough sketch and a one-line description. The principle holds just as much for AI as for a human estimator: the output is only as good as the input.",
      },
      {
        type: "p",
        content:
          "The best results come from inputs that include: dimensioned floor plans and elevations, a structural specification (beam sizes, foundation type), material spec (brick type, insulation type, roof finish), and any known ground conditions or access constraints.",
      },
      {
        type: "h3",
        content: "2. Job type and complexity",
      },
      {
        type: "p",
        content:
          "AI tools trained on residential construction data perform well on the job types that make up most of their training set: rear extensions, loft conversions, new-build single-storey structures, and refurbishments. Complex or unusual jobs (historic buildings, hybrid structures, highly bespoke specifications) are where accuracy degrades most.",
      },
      {
        type: "p",
        content:
          "A single-storey rear extension with a flat roof, standard masonry walls, and a timber kit roof is a well-understood job type. An AI tool will typically generate a scope that a human estimator would recognise as complete and plausibly priced. A listed-building renovation with lime mortar, specialist joinery, and conservation planning conditions is a different challenge.",
      },
      {
        type: "h3",
        content: "3. Market calibration",
      },
      {
        type: "p",
        content:
          "Labour rates and material costs vary significantly across UK regions. A tool calibrated on London rates will systematically overestimate in the North West; one calibrated on national averages will underestimate in prime London postcodes. When evaluating any AI estimating tool, check whether it uses UK-specific pricing and whether you can adjust rates to match your local market.",
      },
      {
        type: "h3",
        content: "4. Scope completeness",
      },
      {
        type: "p",
        content:
          "A common failure mode for AI estimating is not pricing individual items inaccurately, but missing items entirely. Drainage connections, structural engineer fees, Building Control applications, temporary works, and scaffolding are frequently omitted from AI-generated scopes if they are not explicitly mentioned in the input. A tool with built-in validation checks (flagging likely missing items) is significantly more reliable than one that only generates what was implied.",
      },
      {
        type: "h2",
        content: "UK-specific considerations",
      },
      {
        type: "p",
        content:
          "UK construction has regulatory and market features that AI tools need to handle correctly to be useful for British builders:",
      },
      {
        type: "ul",
        content: [
          "Building Regulations compliance costs (Part L insulation requirements, Part F ventilation, structural certification) add significant cost to most jobs and must be included in any realistic estimate.",
          "VAT treatment varies. Most residential new-build is zero-rated, alterations and extensions are standard-rated at 20 percent, and some energy-efficiency works qualify for the 5 percent reduced rate. An AI tool that applies the wrong VAT rate gives a misleading total.",
          "Scaffold costs in the UK are substantial. A typical domestic scaffold for a rear extension runs to £1,500 to £3,000 depending on height and duration, and is often underestimated or omitted entirely.",
          "Skip hire, waste disposal, and Section 59 road licences are regularly missed in AI-generated scopes and represent a real cost on most jobs.",
          "Party Wall Act surveyor fees apply to most extension projects in semi-detached and terraced properties and can add £1,000 to £3,000 per neighbour.",
        ],
      },
      {
        type: "p",
        content:
          "An AI estimating tool built specifically for the UK market, rather than adapted from a global product, will handle these considerations by default.",
      },
      {
        type: "h2",
        content: "AI vs manual estimating: an honest comparison",
      },
      {
        type: "p",
        content:
          "Manual estimating by an experienced QS or builder produces the highest accuracy, but takes time, costs money, and still carries uncertainty. A detailed cost plan from a professional QS might cost £500 to £2,000 for a domestic project and take several days. Even then, final costs regularly deviate by 10 to 15 percent.",
      },
      {
        type: "p",
        content:
          "AI estimating produces a structured first draft in minutes at a fraction of the cost. The output requires human review (checking for missing scope items, adjusting rates to reflect current supplier quotes, and applying local market knowledge) but starts from a structured, numbered line-item breakdown rather than a blank page.",
      },
      {
        type: "p",
        content:
          "The practical comparison is not AI versus a detailed QS cost plan. It is AI versus the builder doing the estimate themselves at the kitchen table on a Tuesday evening. On that basis, a good AI tool typically produces a more complete and consistently structured scope than most manual spreadsheet estimates, in far less time.",
      },
      {
        type: "h2",
        content: "When to trust AI-generated estimates",
      },
      {
        type: "p",
        content:
          "AI estimating output is most reliable and most appropriate to use in the following situations:",
      },
      {
        type: "ol",
        content: [
          "Early feasibility: The client wants a rough order of magnitude before committing to detailed design. AI gives a fast, structured ballpark that is more useful than a back-of-envelope figure.",
          "Quote competition: You need to decide whether a job is worth pursuing before investing time in a detailed estimate. AI output tells you quickly whether the numbers are likely to work.",
          "Template and standard-job types: Rear extensions, loft conversions, and similar well-defined job types where AI has been trained on similar scopes and the output is consistently complete.",
          "First draft for review: You use the AI output as a structured starting point, then apply your knowledge of the specific site, your supplier rates, and your current workload. The AI does the heavy lifting; you apply the expertise.",
        ],
      },
      {
        type: "p",
        content:
          "AI-generated estimates should be reviewed carefully before being sent to a client as a final quote, particularly on complex jobs, jobs with unusual ground conditions, or anything involving significant structural work. The same critical review applies to any estimate, however it was produced.",
      },
      {
        type: "h2",
        content: "How The Proper Tool approaches accuracy",
      },
      {
        type: "p",
        content:
          "The Proper Tool is built specifically for UK residential construction. It reads architectural drawings, structural engineer reports, site photos, and plain-text descriptions to generate a structured line-item quote with quantities and UK market rates across 31 trade categories.",
      },
      {
        type: "p",
        content:
          "A built-in validation layer checks every generated quote for common scope omissions, quantity anomalies, and pricing that is out of range for the job type. Validation flags are shown to the builder before the quote is sent, so missing items are caught during review rather than discovered on site.",
      },
      {
        type: "p",
        content:
          "The result is a first draft that experienced builders describe as 80 to 90 percent of the way to a quote they would send, reviewed and adjusted in minutes rather than hours.",
      },
    ],
    faqs: [
      {
        q: "How accurate is AI estimating compared to a professional quantity surveyor?",
        a: "On well-defined residential jobs with good input drawings, an AI estimating tool will typically produce totals within 10 to 20 percent of a professional QS cost plan. A formal QS cost plan at RIBA Stage 3 carries a typical tolerance of plus or minus 10 to 15 percent itself, so for early-stage feasibility and quoting purposes the gap is smaller than most builders expect. The key difference is that an AI estimate takes minutes and a QS cost plan takes days.",
      },
      {
        q: "What type of jobs does AI estimating work best for?",
        a: "AI estimating performs best on well-understood residential job types: rear extensions, loft conversions, single-storey structures, and standard refurbishments. These job types are well-represented in training data and follow predictable scope patterns. Complex or bespoke jobs (listed buildings, highly unusual structural configurations, specialist finishes) require more human review and the AI output should be treated as a structured starting point rather than a near-final estimate.",
      },
      {
        q: "Does AI estimating work for UK-specific costs and regulations?",
        a: "It depends on the tool. AI estimating tools built for the UK market will use UK labour rates, UK material costs, and account for UK-specific costs like Building Regulations, VAT treatment, scaffold hire, and Party Wall Act fees. Global or US-origin tools adapted for the UK often miss these specifics. Always check that any AI estimating tool you use is calibrated for the UK market.",
      },
      {
        q: "Can I send an AI-generated estimate directly to a client?",
        a: "Not without review. AI-generated estimates should be treated as a structured first draft. Before sending to a client, check that the scope is complete for the specific job, verify that material and labour rates reflect your current costs, and confirm that any job-specific constraints (access, ground conditions, neighbour agreements) are accounted for. The review typically takes minutes rather than hours, but it is an essential step.",
      },
      {
        q: "What information do I need to give an AI estimating tool to get accurate results?",
        a: "The more information you provide, the more accurate the output. At a minimum: a description of the work and approximate dimensions. For better accuracy: dimensioned floor plans, elevations, and a material specification. For best accuracy: a full architectural drawing set, structural engineer's report, and any known site constraints. A vague one-line description will produce a rough ballpark; a complete drawing set will produce a detailed line-item scope.",
      },
      {
        q: "How does AI estimating handle items it cannot see in the drawings?",
        a: "Good AI estimating tools use two approaches: they infer likely missing items based on the job type (for example, assuming scaffold will be needed for a two-storey extension even if not drawn), and they flag potential gaps with validation warnings for the builder to review. Tools without a validation layer may simply omit items not explicitly visible in the drawings, which leads to incomplete scopes.",
      },
      {
        q: "Is AI estimating suitable for loft conversions and extensions specifically?",
        a: "Yes. These are the job types where AI estimating is most reliable. Rear extensions and loft conversions are well-defined, follow standard scope patterns, and are well-represented in training data. An AI tool will typically generate a complete line-item scope for these job types with minimal gaps, covering structural works, insulation, drainage, first fix, finishes, and external works. The builder's review is still important but the starting point is strong.",
      },
    ],
    relatedLinks: [
      { href: "/auto-quote", title: "Try AI Auto Quoting: generate a quote from drawings in minutes" },
      { href: "/ai-quoting-software", title: "AI Quoting Software for UK Builders" },
      { href: "/estimating-software-for-builders", title: "Estimating Software for Builders: What to Look For" },
      { href: "/blog/how-to-price-building-jobs", title: "How to Price Building Jobs: A Practical Guide for UK Builders" },
      { href: "/best/quoting-software-for-uk-builders", title: "Best Quoting Software for UK Builders (2026 Comparison)" },
    ],
  },
  {
    slug: "can-ai-quote-from-a-photo",
    title: "Can AI Quote a Building Job from a Photo?",
    metaTitle: "Can AI Quote from a Photo? What Works and What Doesn't (2026)",
    metaDescription:
      "Can AI generate a construction quote from a photo alone? An honest look at what photo-based AI estimating can and can't do, and how to get better results.",
    publishedDate: "2026-07-01",
    modifiedDate: "2026-07-01",
    excerpt:
      "A photo can be enough to get a rough AI-generated estimate started, but it rarely tells the whole story. Here's what AI can actually read from a photo, where it falls short, and how to combine photos with other inputs for a usable quote.",
    sections: [
      {
        type: "p",
        content:
          "It's a tempting idea: snap a photo on site, upload it, and get a priced quote back in minutes. Some AI estimating tools do accept photos as an input, and modern vision models can genuinely extract useful information from an image. But 'can it work from a photo' and 'should you rely on a photo alone' are two different questions.",
      },
      {
        type: "h2",
        content: "What AI can actually read from a photo",
      },
      {
        type: "p",
        content:
          "A single photo of a room, wall, or elevation gives an AI model visual cues about materials, condition, and approximate scale, but very little hard dimensional data. Vision models can typically identify:",
      },
      {
        type: "ul",
        content: [
          "Broad material types: brick versus render, tile versus slate roof, timber versus uPVC windows.",
          "Visible condition issues: cracked render, damp staining, sagging guttering, missing pointing.",
          "Rough room type and layout cues: kitchen, bathroom, existing extension footprint.",
          "Approximate relative scale when a reference object (a standard door, a brick course) is visible in frame.",
        ],
      },
      {
        type: "p",
        content:
          "What a photo cannot reliably give an AI model is exact dimensions. A photo has no scale bar. Without a dimensioned drawing or a stated measurement, any quantity the AI infers (wall length, floor area, roof pitch) is an estimate based on visual proportion, not a measurement.",
      },
      {
        type: "h2",
        content: "Where photo-only quoting breaks down",
      },
      {
        type: "h3",
        content: "1. Quantities are guesses, not measurements",
      },
      {
        type: "p",
        content:
          "Materials in construction are priced by quantity: square metres of plaster, linear metres of skirting, cubic metres of concrete. A photo-derived quantity is a visual estimate. On a small job the margin of error might be tolerable; on a full extension or loft conversion, a 20 percent error in floor area translates directly into a 20 percent error in the materials bill.",
      },
      {
        type: "h3",
        content: "2. Hidden and structural elements aren't visible",
      },
      {
        type: "p",
        content:
          "A photo shows the surface. It cannot show what's behind a wall, what size the joists are, whether there's a cavity, or what the foundations look like. Any job involving structural changes (removing a wall, adding a beam, underpinning) needs a structural specification, not a photo, to be quoted safely.",
      },
      {
        type: "h3",
        content: "3. Multiple angles rarely add up to a full scope",
      },
      {
        type: "p",
        content:
          "Even a set of photos from different angles doesn't reconstruct a full 3D floor plan the way a drawing does. AI can stitch together a general impression of a space, but it will still be filling gaps with assumptions rather than facts.",
      },
      {
        type: "h2",
        content: "How to get a usable quote when you only have photos",
      },
      {
        type: "p",
        content:
          "Photos are genuinely useful, just not as a standalone input. The most reliable approach combines photos with a small amount of additional information:",
      },
      {
        type: "ol",
        content: [
          "Add a written description alongside the photos: room dimensions if you have them, the scope of work, and any known specification details (paint type, insulation thickness, boiler model).",
          "Include at least one measurement per major surface, even a rough tape-measure figure. This gives the AI a scale reference to work from instead of a visual guess.",
          "Use photos to support drawings, not replace them. If a drawing exists, upload it alongside site photos so the AI can cross-check the drawn dimensions against the real-world condition.",
          "Treat photo-derived quotes as a starting point for a site visit conversation with the client, not a number to send as a fixed price.",
        ],
      },
      {
        type: "callout",
        content:
          "The most reliable AI-generated quotes combine a photo (for material and condition context) with a written description or drawing (for dimensions and scope). Photos alone can support a rough ballpark; they are not enough for an accurate, sendable quote.",
      },
      {
        type: "h2",
        content: "How The Proper Tool handles photo input",
      },
      {
        type: "p",
        content:
          "The Proper Tool accepts site photos as one of several inputs alongside drawings and plain-text descriptions. Photos help the AI understand material types, existing condition, and site context, while dimensions and scope come from drawings or the written description whenever they are available. When only photos are provided, the generated quote is flagged for closer review, and the validation layer highlights any line items where quantities were inferred rather than measured.",
      },
    ],
    faqs: [
      {
        q: "Can AI generate an accurate quote from just one photo?",
        a: "Not reliably. A single photo can tell an AI model about materials and visible condition, but it has no scale reference, so any quantities (wall area, floor area) are visual estimates rather than measurements. A photo works well as supporting context alongside a written description or drawing, but is not enough on its own for an accurate quote.",
      },
      {
        q: "What can AI actually see in a construction photo?",
        a: "AI vision models can typically identify material types (brick vs render, tile vs slate), visible condition issues (cracks, damp, sagging gutters), and rough layout cues. They struggle with exact dimensions, anything hidden behind a surface (cavity walls, joist sizes, foundations), and precise quantities.",
      },
      {
        q: "Do I need a drawing as well as photos to get a good AI quote?",
        a: "For anything beyond a rough ballpark, yes. Drawings provide dimensions and scope that photos cannot. The most reliable results combine a drawing (or at minimum a written description with measurements) with photos for material and condition context.",
      },
      {
        q: "Can AI tell what materials are used just from a photo?",
        a: "Generally yes, for visible surface materials like brick type, render finish, roof tile type, and window frame material. It cannot see materials hidden behind finishes, such as insulation type inside a cavity or the specification of structural timber.",
      },
      {
        q: "Is a photo-based AI quote good enough to send to a client?",
        a: "Treat it as a starting point rather than a final figure. A photo-only quote should be reviewed and adjusted with at least a rough set of measurements before being sent as a formal quote, since quantities derived purely from a photo carry meaningful uncertainty.",
      },
      {
        q: "How many photos should I upload for the best AI estimate?",
        a: "More angles help, but the bigger improvement comes from adding a measurement or two and a short written description alongside the photos, rather than uploading many photos with no dimensional context. Three or four clear photos covering the main elevations or rooms, plus a rough dimension set, is usually enough to move from a vague guess to a workable draft quote.",
      },
    ],
    relatedLinks: [
      { href: "/quote-from-photo", title: "Quote from Photo: Generate a Quote from Site Photos" },
      { href: "/quote-from-drawings", title: "Quote from Drawings: Upload Plans for an Instant Quote" },
      { href: "/faq/how-accurate-is-ai-estimating", title: "How Accurate Is AI Estimating for UK Builders?" },
      { href: "/faq/can-ai-read-construction-plans", title: "Can AI Read Construction Plans?" },
      { href: "/ai-takeoff-software", title: "AI Takeoff Software for UK Builders" },
    ],
  },
  {
    slug: "can-ai-read-construction-plans",
    title: "Can AI Read Construction Plans and Architectural Drawings?",
    metaTitle: "Can AI Read Construction Plans? A UK Builder's Guide (2026)",
    metaDescription:
      "Can AI actually read architectural drawings and construction plans? How AI extracts dimensions, specifications, and structural details from PDF and CAD plans.",
    publishedDate: "2026-07-01",
    modifiedDate: "2026-07-01",
    excerpt:
      "Modern AI models can read dimensioned architectural drawings, extract structural specifications, and use them to build a priced quote. Here's how it actually works, what formats are supported, and where the process still needs a human check.",
    sections: [
      {
        type: "p",
        content:
          "Reading a drawing is one of the core skills an estimator needs, and it's also one of the tasks AI has become genuinely capable of in the last few years. Rather than treating a PDF drawing as a flat image, modern AI estimating tools use a combination of document parsing and vision models to extract the structured information a drawing contains.",
      },
      {
        type: "h2",
        content: "What formats can AI read?",
      },
      {
        type: "p",
        content:
          "Most AI estimating tools accept the same formats builders already receive from architects and structural engineers:",
      },
      {
        type: "ul",
        content: [
          "PDF drawings — the most common format for floor plans, elevations, and sections received from architects.",
          "Scanned or photographed plans — lower accuracy than a native PDF, but usable if the resolution is good and the plan is reasonably flat and well-lit.",
          "Structural engineer's calculations and specifications — often supplied as a separate PDF report with beam sizes, foundation details, and load calculations.",
          "CAD exports in some tools, though PDF remains the most universally supported format across the industry.",
        ],
      },
      {
        type: "h2",
        content: "What information AI extracts from a drawing",
      },
      {
        type: "h3",
        content: "1. Dimensions and floor area",
      },
      {
        type: "p",
        content:
          "Dimensioned drawings include measurement lines with figures written along them. AI models can read these figures and associate them with the correct wall or room, allowing calculation of floor areas, wall lengths, and room dimensions without the estimator manually measuring the drawing.",
      },
      {
        type: "h3",
        content: "2. Structural specifications",
      },
      {
        type: "p",
        content:
          "Structural notes on a drawing or in an accompanying engineer's report typically specify beam sizes (for example '203 x 133 UB' or '2 No. 178 UB'), padstone requirements, and foundation types. AI models trained on construction terminology can parse these references and translate them into priced line items, including the correct quantity of steel beams and associated padstones.",
      },
      {
        type: "h3",
        content: "3. Room labels and material notes",
      },
      {
        type: "p",
        content:
          "Drawings often label rooms (kitchen, bathroom, utility) and include material call-outs (insulation type, external finish, roof covering). AI models can read this text and use it to select the correct trade categories and materials for the quote.",
      },
      {
        type: "h3",
        content: "4. Sections and elevations",
      },
      {
        type: "p",
        content:
          "Section drawings show vertical detail: roof pitch, floor-to-ceiling heights, foundation depth. This information feeds quantities that a plan view alone cannot provide, such as roof area (which depends on pitch, not just footprint) and wall height for costing brickwork or plastering.",
      },
      {
        type: "h2",
        content: "Where AI drawing reading still has limits",
      },
      {
        type: "p",
        content:
          "AI drawing extraction is strong but not infallible. Common failure points include:",
      },
      {
        type: "ul",
        content: [
          "Poor scan quality: low-resolution or skewed scans make dimension text hard to read reliably.",
          "Hand-annotated changes: pen markups added after the drawing was printed are harder for AI to parse consistently than typed dimensions.",
          "Ambiguous or missing dimensions: if a wall length isn't explicitly dimensioned and must be inferred from a scale bar, accuracy drops.",
          "Conflicting information across sheets: when a floor plan and a structural drawing disagree slightly (a common real-world occurrence), the AI needs to flag the conflict rather than silently pick one.",
        ],
      },
      {
        type: "callout",
        content:
          "Dimensioned CAD-originated PDF drawings from an architect or engineer give AI models the highest read accuracy. Photographed or heavily hand-annotated plans still work but should be treated as lower-confidence input, worth a closer manual check before the quote goes out.",
      },
      {
        type: "h2",
        content: "How The Proper Tool reads plans",
      },
      {
        type: "p",
        content:
          "The Proper Tool reads architectural drawings and structural engineer reports directly, extracting dimensions, room labels, and structural specifications (including beam sizes and padstone counts) to build a line-item quote. A validation layer cross-checks structural quantities mentioned in the description against what's parsed from the drawing, flagging mismatches such as a beam referenced in the text but missing from the priced items, so gaps are caught before the quote is reviewed.",
      },
    ],
    faqs: [
      {
        q: "What file formats can AI read for construction plans?",
        a: "Most AI estimating tools accept PDF drawings (the most common format from architects), scanned or photographed plans, and structural engineer reports. Native PDF drawings give the highest accuracy because the dimension text is digitally embedded rather than needing to be read from a scanned image.",
      },
      {
        q: "Can AI extract exact dimensions from a floor plan?",
        a: "Yes, when the drawing includes dimension lines with figures. AI models can read the measurement text and associate it with the correct wall or room. Accuracy is highest on clean, dimensioned CAD-originated PDFs and lower on rough or hand-annotated sketches.",
      },
      {
        q: "Can AI read structural engineer drawings and beam specifications?",
        a: "Yes. AI models trained on construction terminology can parse structural notes such as beam sizes, quantities, and padstone requirements from an engineer's drawing or calculation report and translate them into priced quote line items.",
      },
      {
        q: "Does AI understand architectural symbols and abbreviations?",
        a: "Modern AI estimating tools are trained on common UK construction drawing conventions, including standard abbreviations for materials, room types, and structural elements. Recognition is strongest for widely used conventions and weaker for firm-specific or unusual notation.",
      },
      {
        q: "What happens if a drawing is low quality or hand-marked-up?",
        a: "AI can still attempt to read a poor-quality or hand-annotated drawing, but accuracy drops. Good AI estimating tools flag lower-confidence extractions so a human can double-check them, rather than silently guessing and presenting the result as fact.",
      },
      {
        q: "Can AI read a full architectural drawing set including sections?",
        a: "Yes, and it should be given the full set where possible. Section and elevation drawings provide vertical information (roof pitch, wall heights) that a plan view alone doesn't capture, and using them improves the accuracy of roof area, wall area, and height-dependent quantities.",
      },
    ],
    relatedLinks: [
      { href: "/quote-from-drawings", title: "Quote from Drawings: Upload Plans for an Instant Quote" },
      { href: "/ai-takeoff-software", title: "AI Takeoff Software for UK Builders" },
      { href: "/faq/how-accurate-is-ai-estimating", title: "How Accurate Is AI Estimating for UK Builders?" },
      { href: "/faq/can-ai-quote-from-a-photo", title: "Can AI Quote a Building Job from a Photo?" },
      { href: "/ai-construction-estimating-software", title: "AI Construction Estimating Software" },
    ],
  },
  {
    slug: "can-ai-learn-my-pricing",
    title: "Can AI Learn My Own Pricing and Rates?",
    metaTitle: "Can AI Learn My Pricing? Custom Rates in AI Quoting Software (2026)",
    metaDescription:
      "Can AI quoting software learn your own material and labour rates instead of generic averages? How custom pricing, rate overrides, and cost libraries work.",
    publishedDate: "2026-07-01",
    modifiedDate: "2026-07-01",
    excerpt:
      "Generic market rates get you close, but every builder has their own suppliers, labour rates, and markups. Here's how AI quoting tools learn and apply your own pricing over time instead of defaulting to national averages forever.",
    sections: [
      {
        type: "p",
        content:
          "One of the most common concerns builders raise about AI quoting is that it will price jobs using generic national averages that don't reflect their actual costs. It's a fair concern; a tool that only ever quotes off a fixed national rate table will drift away from what a specific builder actually pays and charges. The good news is that this is a solved problem in well-built AI quoting tools, through what's usually called a cost library or rate override system.",
      },
      {
        type: "h2",
        content: "How AI quoting tools start: default market rates",
      },
      {
        type: "p",
        content:
          "When you first use an AI quoting tool, it needs somewhere to start. This is typically a default cost library, built from aggregated UK market rate data across trade categories, material types, and regions. Default rates give a new user a usable starting point without having to manually price every single line item before generating their first quote.",
      },
      {
        type: "h2",
        content: "How custom pricing works",
      },
      {
        type: "h3",
        content: "1. Rate overrides at the task or category level",
      },
      {
        type: "p",
        content:
          "The core mechanism is a rate override: instead of using the default rate for, say, 'supply and fit plasterboard ceiling', you enter your own rate based on what you actually pay your plasterer and merchant. Once set, that override applies to every future quote automatically, without needing to be re-entered.",
      },
      {
        type: "h3",
        content: "2. Custom line items for your own specification",
      },
      {
        type: "p",
        content:
          "Beyond overriding existing rates, most tools let you add entirely custom cost items, such as a specific product you always specify or a bespoke labour rate for a subcontractor you regularly use. These custom items then become available for the AI to select when it recognises a matching scope item in future jobs.",
      },
      {
        type: "h3",
        content: "3. Learning from edits over time",
      },
      {
        type: "p",
        content:
          "Some tools go a step further and learn from the edits you make to generated quotes. If you consistently adjust the AI's suggested rate for a particular item, the tool can surface that pattern and suggest making it a permanent override, gradually converging on rates that reflect your actual business rather than the national average.",
      },
      {
        type: "h3",
        content: "4. Regional and market calibration",
      },
      {
        type: "p",
        content:
          "Even before individual overrides, good tools let you select your region or market so that default rates start closer to your local reality. A builder in Manchester and a builder in central London have very different baseline costs; regional calibration narrows that gap before you've entered a single custom rate.",
      },
      {
        type: "callout",
        content:
          "A cost library that supports rate overrides, custom line items, and regional calibration converges toward your actual pricing much faster than a tool using a single fixed national rate table. The first few quotes will need the most correction; after that, the tool increasingly reflects what you actually charge.",
      },
      {
        type: "h2",
        content: "What to check before relying on custom rates",
      },
      {
        type: "ul",
        content: [
          "Do overrides persist automatically across future quotes, or do you have to re-enter them every time? Persistence is the difference between a genuine cost library and a one-off manual edit.",
          "Can you override at both the category level (e.g. all plastering labour) and the individual task level (e.g. one specific ceiling type)?",
          "Does the tool support separate labour, material, and plant rates, so you can adjust one without distorting the others?",
          "Can multiple team members share the same rate library, so pricing stays consistent across the business?",
        ],
      },
      {
        type: "h2",
        content: "How The Proper Tool handles custom pricing",
      },
      {
        type: "p",
        content:
          "The Proper Tool starts every builder with a default UK cost library covering 31 trade categories, then lets you override any task's rate, split it into labour, material, and plant costs, or add entirely custom cost items. Overrides apply automatically to every future Auto, Template, and Manual quote, so the tool's output moves closer to your real pricing with every rate you adjust, rather than resetting to generic averages each time.",
      },
    ],
    faqs: [
      {
        q: "Will AI quoting software use my own material and labour rates?",
        a: "Yes, in tools built with a proper cost library. You start with default market rates, then override individual tasks or categories with your own pricing. Once set, overrides apply automatically to every future quote rather than needing to be re-entered each time.",
      },
      {
        q: "Do I have to manually price every item to use my own rates?",
        a: "No. You only need to override the items where your actual cost differs meaningfully from the default. Everything else continues to use the default market rate until you choose to adjust it, so you can start using custom pricing incrementally.",
      },
      {
        q: "Can I set different rates for labour, material, and plant separately?",
        a: "In a well-built cost library, yes. Splitting a rate into its labour, material, and plant components lets you adjust one part (for example, a material price increase from your merchant) without distorting your labour rate.",
      },
      {
        q: "Does the AI learn from the changes I make to quotes over time?",
        a: "Some tools track edits you make to generated quotes and surface patterns, suggesting a permanent rate override when you consistently adjust the same item. This isn't universal across all AI quoting software, so it's worth checking whether a specific tool supports it.",
      },
      {
        q: "Can my whole team use the same custom rates?",
        a: "In tools built for teams, yes: a shared cost library means every user generates quotes off the same rate set, keeping pricing consistent whether a quote is drafted by the business owner or a project manager.",
      },
      {
        q: "What happens to my custom rates if I switch to a different job type or market?",
        a: "Custom rate overrides are typically tied to your account and cost library, not to a single quote, so they carry across job types. If you operate in multiple regions or markets, check whether the tool supports separate rate sets per market, since UK, US, Australian, and Canadian labour and material costs differ substantially.",
      },
    ],
    relatedLinks: [
      { href: "/pricing", title: "The Proper Tool Pricing Plans" },
      { href: "/faq/how-accurate-is-ai-estimating", title: "How Accurate Is AI Estimating for UK Builders?" },
      { href: "/estimating-software-for-builders", title: "Estimating Software for Builders: What to Look For" },
      { href: "/blog/how-to-price-building-jobs", title: "How to Price Building Jobs: A Practical Guide for UK Builders" },
      { href: "/for-small-builders", title: "AI Quoting Software for Small Builders" },
    ],
  },
  {
    slug: "will-ai-replace-estimators",
    title: "Will AI Replace Construction Estimators?",
    metaTitle: "Will AI Replace Estimators? What the Evidence Actually Shows (2026)",
    metaDescription:
      "Will AI replace construction estimators and quantity surveyors? A realistic look at what AI automates, what still needs a human, and how the estimator's role is changing.",
    publishedDate: "2026-07-01",
    modifiedDate: "2026-07-01",
    excerpt:
      "AI is automating a lot of the repetitive work in construction estimating, but it isn't replacing the judgement, client relationships, and risk assessment that experienced estimators bring. Here's a realistic look at what's actually changing.",
    sections: [
      {
        type: "p",
        content:
          "It's a natural question whenever a new tool starts doing something a skilled professional used to do by hand: does this replace the job? For construction estimating, the honest answer is more nuanced than yes or no. AI is genuinely automating a meaningful chunk of estimating work, but the parts it automates and the parts it doesn't tell you a lot about where the profession is actually heading.",
      },
      {
        type: "h2",
        content: "What AI is genuinely automating",
      },
      {
        type: "p",
        content:
          "The repetitive, time-consuming parts of estimating are the ones AI handles well: reading drawings and extracting dimensions, applying standard rates to quantities, structuring a scope of work into trade categories, and producing a first-draft priced document in minutes instead of hours. For a small builder who previously spent an evening manually pricing a straightforward extension, this is a substantial and genuine time saving.",
      },
      {
        type: "h2",
        content: "What AI is not replacing",
      },
      {
        type: "h3",
        content: "1. Judgement on unusual or high-risk jobs",
      },
      {
        type: "p",
        content:
          "AI performs best on well-understood, standard job types. A listed building with unknown structural conditions, a job with complex party wall implications, or a site with known ground contamination all require judgement calls that draw on experience with similar past jobs, not pattern-matching against training data. Experienced estimators bring risk assessment that AI cannot replicate reliably.",
      },
      {
        type: "h3",
        content: "2. Client relationships and negotiation",
      },
      {
        type: "p",
        content:
          "A quote is rarely the end of a conversation. Clients ask questions, want changes explained, negotiate on scope and price, and need reassurance about a builder's approach. That relationship-building and communication work sits entirely with the human, and it's often the deciding factor in whether a quote turns into a signed job.",
      },
      {
        type: "h3",
        content: "3. Accountability for the final number",
      },
      {
        type: "p",
        content:
          "When a job overruns, someone is accountable for the estimate that was given. AI-generated numbers still need a human to review, adjust, and ultimately stand behind before they go to a client. That accountability, and the professional judgement that underpins it, remains with the estimator or builder, not the tool.",
      },
      {
        type: "h3",
        content: "4. On-site knowledge and local supplier relationships",
      },
      {
        type: "p",
        content:
          "Experienced estimators carry knowledge that isn't written down anywhere an AI model could learn it from: which local supplier gives the best price on a given week, which subcontractors are reliable for a specific type of work, and site-specific quirks from having visited similar properties before. This tacit knowledge continues to differentiate a good estimator from a generic pricing exercise.",
      },
      {
        type: "h2",
        content: "How the estimator's role is actually changing",
      },
      {
        type: "p",
        content:
          "The pattern emerging across the industry looks less like replacement and more like a shift in where time goes. Estimators and builders using AI tools spend less time on manual data entry and rate lookup, and more time on reviewing AI output, exercising judgement on flagged risks, and having the client conversations that actually close jobs. The estimating function doesn't disappear; it moves up the value chain toward review, judgement, and relationship management.",
      },
      {
        type: "p",
        content:
          "For sole traders and small building firms, this shift is particularly significant. Many small builders previously couldn't justify the time cost of a detailed written quote for every enquiry and either under-quoted from memory or lost the job to a competitor who quoted faster. AI estimating tools make a detailed, structured quote achievable for every enquiry, which is a net expansion of what's possible rather than a replacement of existing capacity.",
      },
      {
        type: "callout",
        content:
          "The clearest pattern in construction and other estimating-heavy professions is that AI automates the mechanical part of the task (data extraction, rate application, document structuring) while judgement, risk assessment, and client relationships remain firmly human. The job changes shape; it doesn't disappear.",
      },
      {
        type: "h2",
        content: "The Proper Tool's approach: augmentation, not replacement",
      },
      {
        type: "p",
        content:
          "The Proper Tool is built to produce a structured first-draft quote quickly, not to remove the builder from the process. Every AI-generated quote passes through a validation layer that flags likely gaps and anomalies for human review, and the builder always has full control to edit, override, and approve before a quote reaches a client. The tool handles the repetitive extraction and pricing work; the judgement calls and the relationship with the client stay exactly where they belong.",
      },
    ],
    faqs: [
      {
        q: "Will AI take over construction estimating jobs completely?",
        a: "The evidence points to automation of the repetitive parts of estimating (drawing extraction, rate application, document structuring) rather than full replacement. Judgement on unusual or high-risk jobs, client relationships, and accountability for the final number remain human tasks.",
      },
      {
        q: "Should estimators be worried about AI quoting tools?",
        a: "The more useful framing is adaptation rather than worry. Estimators who use AI tools to handle repetitive drafting work free up time for the higher-value parts of the job: reviewing flagged risks, negotiating with clients, and applying judgement on complex jobs. Those who ignore the shift risk being slower and less competitive than builders who've adopted the tools.",
      },
      {
        q: "What parts of estimating can AI not do well?",
        a: "AI struggles with judgement calls on unusual or high-risk jobs (listed buildings, unknown ground conditions, complex party wall situations), client negotiation and relationship management, and drawing on tacit local knowledge like which supplier is currently cheapest or which subcontractor is reliable for a specific trade.",
      },
      {
        q: "Does using AI estimating tools mean I need fewer estimators?",
        a: "For many small building firms, AI tools expand what's achievable rather than reducing headcount, by making a detailed written quote realistic for every enquiry instead of only the biggest jobs. For larger firms, it can mean the same team handles a higher volume of enquiries, since less time goes into manual data entry.",
      },
      {
        q: "Is AI estimating a threat to quantity surveyors?",
        a: "AI overlaps most with the mechanical parts of QS work: quantity take-off and rate application. It does not replace the professional judgement, risk allowances, and contractual expertise that a qualified QS applies, particularly on larger or more complex projects. The role is shifting toward review and judgement rather than disappearing.",
      },
      {
        q: "How should builders adapt to AI estimating tools rather than compete with them?",
        a: "Use AI tools for what they're good at (fast first-draft quotes on standard job types) and focus your own time on what AI can't replicate: site visits, client conversations, judgement calls on unusual risks, and building the reputation that wins repeat business. Builders who treat AI as a drafting assistant rather than a threat tend to get the most value from it.",
      },
    ],
    relatedLinks: [
      { href: "/ai-quoting-software", title: "AI Quoting Software for UK Builders" },
      { href: "/faq/how-accurate-is-ai-estimating", title: "How Accurate Is AI Estimating for UK Builders?" },
      { href: "/faq/can-ai-learn-my-pricing", title: "Can AI Learn My Own Pricing and Rates?" },
      { href: "/for-sole-traders", title: "AI Quoting Software for Sole Traders" },
      { href: "/blog/how-to-price-building-jobs", title: "How to Price Building Jobs: A Practical Guide for UK Builders" },
    ],
  },
];

export function getFaqPage(slug: string): FaqPage | undefined {
  return FAQ_PAGES.find((p) => p.slug === slug);
}
